import React from "react";
import {SiteNavigate} from "nav/SiteLink";
import {PanelLoading} from "components/FeaturePanel";
import {useAuthenticatedSession, useUser} from "./UserProvider";
import {PrivateRouteAccess, privateRouteAccess} from "./auth/privateRouteAccess";

/**
 * Wraps a private route's element: renders it only when the app holds a session that authorizes one,
 * otherwise redirects to the home screen. These routes are intentionally NOT pre-rendered (no SEO
 * value, gated by Firebase auth) and stay fully client-side rendered.
 *
 * Gating is on login alone — "enable all routes by default after signup". A freshly-signed-up user
 * can reach every app route (and, crucially, the /activate page itself) without first holding an
 * active subscription; an active account/subscription would otherwise be a chicken-and-egg
 * requirement for activation. Subscription/account state is surfaced in-app via banners and PRO
 * labels rather than by hard-blocking the route.
 *
 * WHAT login means here is the rule in {@link ./auth/privateRouteAccess}, and it is not `isLoggedIn`
 * alone (ui#2210): that flag is restored from `localStorage`, so it outlives the session it describes
 * and handed an account the api refuses a full page load of the signed-in shell. The undecided frame
 * shows the same in-panel skeleton the five panel routes already show while their chunk loads, so
 * nothing new blanks — `/personalization` renders no body of its own and passes `fallback={null}`.
 */
export default function PrivateRoute({children, fallback = <PanelLoading/>}: {
	children: React.ReactNode;
	fallback?: React.ReactNode;
}) {
	const [user] = useUser();
	const [session] = useAuthenticatedSession();
	switch (privateRouteAccess(user.isLoggedIn, session)) {
		case PrivateRouteAccess.Granted:
			return <>{children}</>;
		case PrivateRouteAccess.Undecided:
			return <>{fallback}</>;
		case PrivateRouteAccess.Denied:
			return <SiteNavigate to="/" replace/>;
	}
}
